import {useCallback, useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useViewport} from '_customHooks/viewport'

const PAGINATION_ELEMENT_QUERY_SELECTOR = '#pagination'
const BUBBLE_CHAT_QUERY_SELECTOR = 'button#embeddedMessagingConversationButton'

enum BUBBLE_CHAT_BOTTOM_POSITION {
    COLLAPSED = 20,
    NOT_COLLAPSED = 80,
}

interface BottomActionsResult {
    isPaginationVisible: boolean
}

/**
 * @name useBottomActions
 *
 * @hook
 *
 * @description
 * This hook contains all the logic about bottom actions and chat bubble interaction.
 *
 * @param {boolean} isPaginationEnabled Indicates if pagination is enabled; this value should be memoized outside.
 * @returns {BottomActionsResult} Result object.
 */
const useBottomActions = (isPaginationEnabled?: boolean): BottomActionsResult => {
    const {isMobile} = useViewport()
    const location = useLocation()

    const paginationRef = useRef<HTMLDivElement | null>(null)
    const chatBubbleRef = useRef<HTMLButtonElement | null>(null)

    const OBSERVER_TIMEOUT = 5000 // Stop looking for bubble chat after 5s
    const SCROLL_TIMEOUT = 200

    let scrollTimeout: NodeJS.Timeout

    const [isPaginationVisible, setIsPaginationVisible] = useState(isPaginationEnabled)

    /**
     * Set chat bubble position to default.
     */
    const resetChatBubblePosition = () => {
        if (chatBubbleRef.current && isMobile) {
            chatBubbleRef.current.style.bottom = `${BUBBLE_CHAT_BOTTOM_POSITION.COLLAPSED}px`
        }
    }

    /**
     * Set chat bubble position to not overlap PLP / Wishlist actions on mobile viewport.
     */
    const setChatBubbleActionsPosition = () => {
        if (isPaginationEnabled && chatBubbleRef.current && paginationRef.current && isMobile) {
            chatBubbleRef.current.style.bottom = `${BUBBLE_CHAT_BOTTOM_POSITION.NOT_COLLAPSED}px`
        }
    }

    /**
     * Handles scroll behaviour to set collapsed or not collapsed
     * position on chat bubble and set pagination element visibility.
     */
    const handleScroll = useCallback(() => {
        if (isPaginationEnabled && paginationRef.current) {
            setIsPaginationVisible(false)
            resetChatBubblePosition()
            clearTimeout(scrollTimeout)
            scrollTimeout = setTimeout(() => {
                if (paginationRef.current!.getBoundingClientRect().top - window.innerHeight > 0) {
                    setIsPaginationVisible(true)
                    setChatBubbleActionsPosition()
                }
            }, SCROLL_TIMEOUT)
        }
    }, [isPaginationEnabled])

    /**
     * In case of pages navigation the references could be already available.
     * This function initializes the chat bubble position for that case.
     */
    const attemptSetupRefs = () => {
        const chatBubble = document.querySelector(BUBBLE_CHAT_QUERY_SELECTOR) as HTMLButtonElement
        const pagination = document.querySelector(PAGINATION_ELEMENT_QUERY_SELECTOR) as HTMLDivElement

        if (chatBubble) chatBubbleRef.current = chatBubble
        if (pagination) paginationRef.current = pagination

        if (chatBubble && pagination) {
            setChatBubbleActionsPosition()
        }
    }

    /**
     * Manages the observation of DOM elements (bubble chat and pagination) for both their creation and visibility status.
     *
     * This hook combines MutationObserver and IntersectionObserver logic:
     *
     * MutationObserver:
     * - Detects the addition of `chatBubble` and `pagination` elements to the DOM.
     * - Automatically disconnects after 10 seconds if elements are not found, preventing indefinite observation.
     *
     * Cleanup:
     * - Ensures all observers are disconnected and timers are cleared when the component is unmounted to prevent memory leaks and unnecessary background processes.
     */
    useEffect(() => {
        if (isMobile) {
            const chatBubbleObserver = new MutationObserver((mutations, observer) => {
                const chatBubble = document.querySelector(BUBBLE_CHAT_QUERY_SELECTOR) as HTMLButtonElement
                if (chatBubble) {
                    chatBubbleRef.current = chatBubble
                    if (paginationRef.current) {
                        setChatBubbleActionsPosition()
                    }
                    observer.disconnect()
                }
            })

            const paginationObserver = new MutationObserver((mutations, observer) => {
                const pagination = document.querySelector(PAGINATION_ELEMENT_QUERY_SELECTOR) as HTMLDivElement
                if (pagination) {
                    paginationRef.current = pagination
                    observer.disconnect()
                }
            })

            chatBubbleObserver.observe(document.body, {childList: true, subtree: true})
            paginationObserver.observe(document.body, {childList: true, subtree: true})

            const chatBubbleTimeout = setTimeout(() => {
                chatBubbleObserver.disconnect()
            }, OBSERVER_TIMEOUT)

            const paginationTimeout = setTimeout(() => {
                paginationObserver.disconnect()
            }, OBSERVER_TIMEOUT)

            const chatBubbleIntersectionObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        resetChatBubblePosition()
                    }
                })
            })

            const paginationIntersectionObserver = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsPaginationVisible(false)
                    } else if (window.scrollY <= paginationRef.current!.offsetTop) {
                        setIsPaginationVisible(true)
                    }
                },
                {threshold: 1.0},
            )

            if (chatBubbleRef.current) {
                chatBubbleIntersectionObserver.observe(chatBubbleRef.current)
            }

            if (paginationRef.current) {
                paginationIntersectionObserver.observe(paginationRef.current)
            }

            // Ensures that chat bubble position is correct on pages navigation
            attemptSetupRefs()

            return () => {
                chatBubbleObserver.disconnect()
                paginationObserver.disconnect()

                clearTimeout(chatBubbleTimeout)
                clearTimeout(paginationTimeout)

                if (chatBubbleRef.current) {
                    resetChatBubblePosition()
                    chatBubbleRef.current = null
                    chatBubbleIntersectionObserver.disconnect()
                }

                if (paginationRef.current) {
                    paginationRef.current = null
                    paginationIntersectionObserver.disconnect()
                }
            }
        }
    }, [location, isPaginationEnabled, isMobile])

    useEffect(() => {
        if (isMobile) {
            window.addEventListener('scroll', handleScroll)

            return () => {
                window.removeEventListener('scroll', handleScroll)
            }
        }
    }, [handleScroll])

    return {isPaginationVisible: isPaginationVisible ?? false}
}

export {useBottomActions}
